import { Select, Text, Textarea, Tr } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import type {
    SystemGeneratedNoteApplication,
    SystemGeneratedNoteConfig,
} from '../../../../hooks/useGetSettings';
import Pagination from '../../../../components/Pagination';
import {
    SettingsSegmentedControl,
    SettingsTable,
    SettingsTableFooter,
    SettingsTd,
    SettingsTh,
    useDynamicPageSize,
} from '../../../../components/settings/primitives';
import type { SettingsSegment } from '../../../../components/settings/primitives';
import NoteCarriersCell, { type CarrierOption } from './NoteCarriersCell';
import { mergeSystemGeneratedNotes, useSelectionDraft } from './shared';
import type { SelectionSectionProps } from './types';

const APPLICATION_SEGMENTS: ReadonlyArray<SettingsSegment<SystemGeneratedNoteApplication>> = [
    { value: 'marketplace', label: 'PartsTrader' },
    { value: 'estimating', label: 'Estimate' },
    { value: 'both', label: 'Both' },
];

const NotesSection = ({ data, insuranceOptions, nonDrpInsuranceOptions }: SelectionSectionProps) => {
    const { selection, updateSelection } = useSelectionDraft(data);
    const [page, setPage] = useState(1);
    // The table owns the viewport height, so the page holds as many notes as fit.
    const { scrollRef, pageSize } = useDynamicPageSize();

    const notes = useMemo(
        () => mergeSystemGeneratedNotes(selection.systemGeneratedNotes),
        [selection.systemGeneratedNotes],
    );

    const totalPages = Math.max(1, Math.ceil(notes.length / pageSize));
    const currentNotes = useMemo(() => {
        const startIndex = (Math.min(page, totalPages) - 1) * pageSize;
        return notes.slice(startIndex, startIndex + pageSize);
    }, [notes, page, pageSize, totalPages]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const drpCarrierOptions = useMemo<CarrierOption[]>(
        () => insuranceOptions.map(option => ({ id: option.insurerId, name: option.insurerName })),
        [insuranceOptions],
    );

    const nonDrpCarrierOptions = useMemo<CarrierOption[]>(
        () => nonDrpInsuranceOptions.map(option => ({ id: option._id, name: option.name })),
        [nonDrpInsuranceOptions],
    );

    const handleUpdateNote = (key: string, updates: Partial<SystemGeneratedNoteConfig>) => {
        updateSelection({
            systemGeneratedNotes: notes.map(note => (note.key === key ? { ...note, ...updates } : note)),
        });
    };

    return (
        <>
            <SettingsTable
                scrollRef={scrollRef}
                fitWidth
                minTableWidth="1240px"
                head={
                    <>
                        <SettingsTh w="16%">SITUATION</SettingsTh>
                        <SettingsTh w="30%">NOTE</SettingsTh>
                        <SettingsTh w="12%">DRPS</SettingsTh>
                        <SettingsTh w="12%">NON-DRP</SettingsTh>
                        <SettingsTh w="9%">CUSTOMER PAY</SettingsTh>
                        <SettingsTh w="21%">APPLICATION DEFAULT</SettingsTh>
                    </>
                }
            >
                {currentNotes.map(note => (
                    // role="group" powers the chevron that fades in on hover
                    // for the editable cells in the row.
                    <Tr key={note.key} role="group" _hover={{ bg: 'gray.25' }}>
                        <SettingsTd>
                            <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                {note.situation}
                            </Text>
                        </SettingsTd>
                        <SettingsTd>
                            {/* Fixed height: the row is sized by the table, so a
                                long note scrolls inside the field rather than
                                letting the user drag the row taller. */}
                            <Textarea
                                value={note.note}
                                onChange={(e) => handleUpdateNote(note.key, { note: e.target.value })}
                                size="sm"
                                rows={2}
                                resize="none"
                                overflowY="auto"
                                bg="white"
                                borderRadius="md"
                                maxLength={499}
                            />
                        </SettingsTd>
                        <SettingsTd>
                            <NoteCarriersCell
                                value={note.drpInsuranceIds}
                                options={drpCarrierOptions}
                                ariaLabel={`Edit ${note.situation} DRP carriers`}
                                onSave={(next) => handleUpdateNote(note.key, { drpInsuranceIds: next })}
                            />
                        </SettingsTd>
                        <SettingsTd>
                            <NoteCarriersCell
                                value={note.nonDrpInsuranceIds}
                                options={nonDrpCarrierOptions}
                                ariaLabel={`Edit ${note.situation} non-DRP carriers`}
                                onSave={(next) => handleUpdateNote(note.key, { nonDrpInsuranceIds: next })}
                            />
                        </SettingsTd>
                        <SettingsTd>
                            <Select
                                size="sm"
                                borderRadius="md"
                                value={note.customerPayEnabled ? 'on' : 'off'}
                                aria-label={`${note.situation} customer pay`}
                                onChange={(e) => handleUpdateNote(note.key, { customerPayEnabled: e.target.value === 'on' })}
                            >
                                <option value="on">On</option>
                                <option value="off">Off</option>
                            </Select>
                        </SettingsTd>
                        <SettingsTd>
                            <SettingsSegmentedControl
                                segments={APPLICATION_SEGMENTS}
                                value={note.applicationDefault}
                                onChange={(value) => handleUpdateNote(note.key, { applicationDefault: value })}
                            />
                        </SettingsTd>
                    </Tr>
                ))}
            </SettingsTable>

            <SettingsTableFooter>
                <Pagination
                    currentPage={Math.min(page, totalPages)}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    py={0}
                />
            </SettingsTableFooter>
        </>
    );
};

export default NotesSection;
